import React from "react";

import ClockCheck from "@uui/ClockCheck";
import ClockSnooze from "@uui/ClockSnooze";
import Users01 from "@uui/Users01";
import UserX01 from "@uui/UserX01";
import XSquare from "@uui/XSquare";
import { DateTime } from "luxon";

import type { SxProps } from "@mui/material";
import Typography from "@mui/material/Typography";

import {
  AttendanceType,
  getAttendanceType,
  Meeting,
} from "@app/analytics/utils";
import type { RequiredBy } from "@app/common/types";

import { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

interface IndicatorStyle {
  backgroundColor: React.CSSProperties["backgroundColor"];
  color: React.CSSProperties["color"];
  icon: typeof ClockCheck;
  label?: string;
  tooltip?: string;
  getLabel?: <T extends Meeting>(meeting?: T) => string;
  getTooltip?: <T extends Meeting>(meeting?: T) => string;
}

type StyleWithStaticLabel = RequiredBy<IndicatorStyle, "label">;
type StyleWithDynamicLabel = RequiredBy<IndicatorStyle, "getLabel">;

const ATTENDANCE_INDICATOR_STYLES: {
  [key in AttendanceType]: StyleWithStaticLabel | StyleWithDynamicLabel;
} = {
  onTime: {
    backgroundColor: "shades.meadow.50",
    color: "shades.meadow.500",
    icon: ClockCheck,
    label: "On-time",
    tooltip: "You were on-time (nice!)",
  },
  late: {
    backgroundColor: "shades.orange.50",
    color: "shades.orange.500",
    icon: ClockSnooze,
    getLabel: (meeting) => {
      if (meeting == null) return "Late";
      return `Late (${DateTime.fromSeconds(meeting?.lateBy ?? 0).toFormat(
        "m:ss"
      )})`;
    },
    getTooltip: (meeting) => {
      if (meeting == null) return "You were late";
      return `You were late (${DateTime.fromSeconds(
        meeting?.lateBy ?? 0
      ).toFormat("m:ss")})`;
    },
  },
  conferenceRoom: {
    backgroundColor: "shades.primary.50",
    color: "shades.primary.500",
    icon: Users01,
    label: "Conf. Room",
    tooltip:
      "Read detected a conference room, so attendance cannot be reported",
  },
  missed: {
    backgroundColor: "shades.neutral.50",
    color: "shades.neutral.400",
    icon: UserX01,
    label: "Absent",
    tooltip: "You were absent",
  },
  declined: {
    backgroundColor: "shades.negative.50",
    color: "shades.negative.400",
    icon: XSquare,
    label: "Declined",
    tooltip: "You declined this meeting",
  },
};

interface BaseAttendanceIndicatorProps<T extends Meeting> {
  count?: number;
  meeting?: T;
  type?: AttendanceType;
  sx?: SxProps;
  showTooltip?: boolean;
  showLabel?: boolean;
}

type AttendanceIndicatorPropsWithMeeting<T extends Meeting> = RequiredBy<
  BaseAttendanceIndicatorProps<T>,
  "meeting"
>;

type AttendanceIndicatorPropsWithType<T extends Meeting> = RequiredBy<
  BaseAttendanceIndicatorProps<T>,
  "type"
>;

export type AttendanceIndicatorProps<T extends Meeting> =
  | AttendanceIndicatorPropsWithMeeting<T>
  | AttendanceIndicatorPropsWithType<T>;

const AttendanceIndicator = <T extends Meeting>({
  showTooltip = false,
  showLabel = false,
  count,
  meeting,
  sx,
  type,
}: AttendanceIndicatorProps<T>) => {
  const indicatorType = type ?? getAttendanceType<T>(meeting);
  if (!indicatorType) return null;

  const {
    color,
    backgroundColor,
    icon: Icon,
    label,
    getLabel,
    tooltip,
    getTooltip,
  } = ATTENDANCE_INDICATOR_STYLES[indicatorType];

  const includeLabel = count == null && showLabel;

  return (
    <TooltipWrapper
      title={getTooltip ? getTooltip(meeting) : tooltip}
      placement="bottom-start"
      show={showTooltip}
      enterDelay={500}
    >
      <Row variant="left" spacing={1}>
        <Row
          variant="center"
          sx={{
            ...sx,
            height: "16px",
            width: "16px",
            px: 0.5,
            borderRadius: "2px",
            backgroundColor,
            color,
          }}
          spacing={0.5}
        >
          <Icon sx={{ color, fontSize: "11px" }} />
          {count != null && (
            <Typography variant="labelReg" color="shades.neutral.700">
              {count}
            </Typography>
          )}
        </Row>
        {includeLabel && (
          <Typography variant="bodySecondaryReg" color="shades.neutral.700">
            {getLabel ? getLabel(meeting) : label}
          </Typography>
        )}
      </Row>
    </TooltipWrapper>
  );
};

export default AttendanceIndicator;
